import { useEffect, useState, useRef } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import CredentialEditorButton from "../components/CredentialEditorButton";
import { UserCog, Plus, Trash2, Save, KeyRound, ShieldCheck, X, Users, Plane, ChevronDown, ChevronUp, Copy, Send, ShieldAlert, Camera, MapPin, Wallet, ClipboardList, Check, Circle, AlertOctagon, Lock } from "lucide-react";
import { fmtDate, fmtDateTime } from "../lib/formatters";

const ROLE_LABEL = {
  FOUNDER: "Founder / CEO", BDOM: "BD & Ops Manager",
  PCWM: "Placement & Cultural Workshop", GEHM: "Guest Experience & Housekeeping",
  SMCM: "Social Media & Content",
};
const ROLES = ["FOUNDER", "BDOM", "PCWM", "GEHM", "SMCM"];
const MODULE_LABEL = {
  program_bookings: "Program Bookings", weekend_bookings: "Weekend Bookings",
  arrivals: "Arrivals", trips: "Trips", activities: "Activities",
  issues: "Issues", rooms: "Rooms", cforms: "C-Forms",
  housekeeping: "Housekeeping", content: "Content Calendar",
  content_analytics: "Content Analytics", sales: "Sales & Invoices",
  partners: "Partners", settings: "Registries", team: "Team",
  receipts_payments: "Receipts & Payments",
  inventory: "Inventory",
  attendance: "Attendance & Salary",
};

export default function Team() {
  const { user, impersonate } = useAuth();
  const isFounder = user.effective_role === "FOUNDER";

  const [users, setUsers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [participantsOpen, setParticipantsOpen] = useState(true);
  const [audit, setAudit] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [editing, setEditing] = useState(null); // user being edited
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "PCWM", destination: "", city: "", permissions: [] });
  const [pwdReset, setPwdReset] = useState({ open: false, uid: null, pwd: "" });
  const [countries, setCountries] = useState([]);

  const load = async () => {
    const [uRes, mRes, pRes, cRes] = await Promise.all([
      api.get("/users"),
      api.get("/team/permissions/modules").catch(() => ({ data: { modules: [] } })),
      api.get("/participants").catch(() => ({ data: [] })),
      api.get("/accommodations/countries").catch(() => ({ data: { countries: [] } })),
    ]);
    setCountries(cRes.data?.countries || []);
    // Team members = only staff roles (Founder, BDOM, PCWM, GEHM, SMCM etc.).
    // Partners and Participants get their own dedicated pages.
    const staff = (uRes.data || []).filter((u) => u.role !== "PARTICIPANT" && u.role !== "PARTNER");
    // Compute is_root_founder on the client from the earliest-created Founder.
    const founders = staff.filter(u => u.role === "FOUNDER").sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const rootId = founders[0]?.id;
    setUsers(staff.map(u => ({ ...u, is_root_founder: u.id === rootId })));
    setModules(mRes.data.modules || []);
    setParticipants(pRes.data || []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isFounder && auditOpen && audit.length === 0) {
      api.get("/audit/security?limit=100").then(({data}) => setAudit(data || [])).catch(()=>{});
    }
  }, [auditOpen, isFounder, audit.length]);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email, password required");
    try {
      await api.post("/team/members", { name: form.name, email: form.email, password: form.password, role: form.role, destination: form.destination, city: form.city });
      toast.success("Team member added");
      setCreating(false);
      setForm({ name: "", email: "", password: "", role: "PCWM", destination: "", city: "", permissions: [] });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const beginEdit = (u) => setEditing({ ...u, permissions: u.permissions || [] });
  const cancelEdit = () => setEditing(null);
  const savePermissions = async () => {
    await api.patch(`/team/members/${editing.id}`, {
      name: editing.name, role: editing.role, permissions: editing.permissions,
      destination: editing.destination || "", city: editing.city || "",
    });
    toast.success("Updated");
    setEditing(null);
    await load();
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/team/members/${u.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitPwd = async () => {
    if (!pwdReset.pwd) return toast.error("Enter a new password");
    await api.patch(`/team/members/${pwdReset.uid}`, { password: pwdReset.pwd });
    toast.success("Password reset");
    setPwdReset({ open: false, uid: null, pwd: "" });
  };

  const togglePerm = (mod) => {
    setEditing(e => ({
      ...e,
      permissions: e.permissions.includes(mod)
        ? e.permissions.filter(p => p !== mod)
        : [...e.permissions, mod],
    }));
  };

  const copyParticipantCreds = async (p) => {
    if (!p?.participant_booking_id) { toast.error("No linked booking"); return; }
    try {
      await api.post(`/participants/${p.participant_booking_id}/create-credentials`);
      const { data } = await api.post(`/participants/${p.participant_booking_id}/send-credentials`, { send_email: false });
      const msg = `Aii Group Participant Portal\n\nApp: ${data.app_url}\nLogin: ${data.email}\nPassword: ${data.password}`;
      await navigator.clipboard.writeText(msg);
      toast.success("Login credentials copied");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendParticipantCreds = async (p) => {
    if (!p?.participant_booking_id) { toast.error("No linked booking"); return; }
    try {
      await api.post(`/participants/${p.participant_booking_id}/create-credentials`);
      const { data } = await api.post(`/participants/${p.participant_booking_id}/send-credentials`, { send_email: true });
      try { await navigator.clipboard.writeText(`App: ${data.app_url}\nLogin: ${data.email}\nPassword: ${data.password}`); }
      catch (err) { console.debug("clipboard write failed:", err?.message); }
      toast.success("Email sent · credentials copied");
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteParticipant = async (p) => {
    if (!p?.participant_booking_id) { toast.error("No linked booking"); return; }
    if (!window.confirm(`Revoke portal access for ${p.name}? Their booking, C-forms and service requests are kept, but they won't be able to sign in anymore.`)) return;
    try {
      await api.delete(`/participants/${p.participant_booking_id}`);
      toast.success(`${p.name} — portal access revoked`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const picRefs = useRef({});
  const uploadMemberPic = async (u, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Picture too large (max 5 MB)"); return; }
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: fileRes } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/team/members/${u.id}`, { profile_pic_id: fileRes.id });
      toast.success(`Updated ${u.name}'s picture`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { if (picRefs.current[u.id]) picRefs.current[u.id].value = ""; }
  };

  // One-click preset: grants Attendance & Salary access to a non-India office
  // staffer (Housekeeping is India-only). Adds the "attendance" permission
  // to the user's list without touching anything else.
  const grantSalaryAccess = async (u) => {
    const next = Array.from(new Set([...(u.permissions || []), "attendance"]));
    try {
      await api.patch(`/team/members/${u.id}`, { permissions: next });
      toast.success(`Salary access granted to ${u.name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revokeSalaryAccess = async (u) => {
    const next = (u.permissions || []).filter(p => p !== "attendance");
    try {
      await api.patch(`/team/members/${u.id}`, { permissions: next });
      toast.success(`Salary access revoked from ${u.name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5">
      {/* Founder-only per-office setup checklist. Derived live from the DB
          so items tick themselves as offices add PCWMs / accommodations /
          fill staff phones — no manual bookkeeping. */}
      {isFounder && <OfficeChecklists onOfficeDeleted={load}/>}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Team</div>
          <h1 className="text-2xl font-semibold">Aii Group Team Members</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">
            {isFounder ? "Add, edit, or remove team members. Grant extra module access per person." : "Founder can view any teammate's dashboard without their password."}
          </p>
        </div>
        {isFounder && (
          <button data-testid="team-add" onClick={() => setCreating(true)} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
            <Plus size={14} /> Add member
          </button>
        )}
      </div>

      {/* Create form */}
      {creating && (
        <div className="aii-card p-5" data-testid="team-create-form">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold">New team member</h3>
            <button onClick={() => setCreating(false)}><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <input data-testid="team-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
            <input data-testid="team-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
            <input data-testid="team-password" type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Initial password" className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
            <select data-testid="team-role" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <select data-testid="team-destination" value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))} className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              <option value="">— Destination (country) —</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="team-city" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="City / office (e.g. Jaipur)" className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
          </div>
          <p className="text-[11px] text-[color:var(--aii-muted)] mt-2 flex items-center gap-1"><MapPin size={11}/> Destination + City auto-scopes every operational list this member sees (Bookings, Arrivals, Trips, Activities, Issues, Housekeeping, Inventory, Weekend Tours).</p>
          <div className="flex justify-end mt-4">
            <button data-testid="team-save" onClick={create} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark">Add member</button>
          </div>
        </div>
      )}

      {/* Team members section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-brand"/>
          <h2 className="font-display font-semibold text-lg">Team members ({users.length})</h2>
        </div>
        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map(u => (
            <div key={u.id} className="aii-card p-5" data-testid={`team-card-${u.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {u.profile_pic_id ? (
                    <img src={`${API}/files/${u.profile_pic_id}?auth=${getToken()}`} alt={u.name}
                         className="w-12 h-12 rounded-full object-cover border-2 border-[color:var(--aii-green-tint)]"
                         data-testid={`team-avatar-${u.id}`}/>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-bold flex items-center justify-center font-display text-lg">{u.name[0]}</div>
                  )}
                  <div>
                    <div className="font-semibold flex items-center gap-1.5">
                      {u.name}
                      {u.locked && (
                        <span data-testid={`team-locked-${u.id}`}
                              title={`Emergency-locked${u.locked_by ? ` by ${u.locked_by}` : ""}${u.locked_at ? ` on ${u.locked_at.slice(0,10)}` : ""}`}
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded font-bold">
                          <Lock size={9}/> LOCKED
                        </span>
                      )}
                      {isFounder && (
                        <>
                          <button onClick={() => picRefs.current[u.id]?.click()} data-testid={`team-pic-${u.id}`}
                                  title="Upload profile picture" className="text-[color:var(--aii-muted)] hover:text-brand p-0.5">
                            <Camera size={12}/>
                          </button>
                          <input type="file" accept="image/*" className="hidden"
                                 ref={(el) => { picRefs.current[u.id] = el; }}
                                 onChange={(e) => uploadMemberPic(u, e.target.files?.[0])}/>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-[color:var(--aii-muted)]">{u.email}</div>
                  </div>
                </div>
                {isFounder && u.id !== user.id && !u.is_root_founder && (
                  <div className="flex gap-1">
                    <button title="Reset password" data-testid={`team-pwd-${u.id}`} onClick={() => setPwdReset({ open: true, uid: u.id, pwd: "" })} className="text-[color:var(--aii-muted)] hover:text-brand p-1"><KeyRound size={14} /></button>
                    <button data-testid={`team-edit-${u.id}`} title="Edit permissions" onClick={() => beginEdit(u)} className="text-[color:var(--aii-muted)] hover:text-brand p-1"><ShieldCheck size={14} /></button>
                    <CredentialEditorButton user={u} onDone={load}/>
                    <button data-testid={`team-del-${u.id}`} title="Delete" onClick={() => del(u)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><Trash2 size={14} /></button>
                  </div>
                )}
                {u.is_root_founder && (
                  <span data-testid={`team-root-${u.id}`} title="Root Founder — protected from deletion" className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded border border-emerald-200 font-semibold">Root</span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="pill pill-green">{ROLE_LABEL[u.role]}</span>
                {(u.destination || u.city) && (
                  <span className="pill pill-amber flex items-center gap-1" data-testid={`team-loc-${u.id}`}>
                    <MapPin size={10}/> {[u.city, u.destination].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
              {u.permissions && u.permissions.length > 0 && (
                <div className="mt-2 text-[11px] text-[color:var(--aii-muted)]">
                  Extra: {u.permissions.map(p => MODULE_LABEL[p] || p).join(", ")}
                </div>
              )}
              {u.role !== "FOUNDER" && isFounder && (
                <button data-testid={`view-as-${u.id}`} onClick={() => impersonate(u.role)}
                  className="mt-4 w-full flex items-center justify-center gap-2 text-sm bg-brand text-white py-2 rounded-md hover:bg-brand-dark font-semibold">
                  <UserCog size={14} /> View this dashboard
                </button>
              )}
              {/* Salary-access preset button — surfaces on non-India staff so
                  Founder can enable Attendance & Salary for them in one click
                  without opening the full permissions modal. India offices
                  already have this access by default. */}
              {isFounder && u.role !== "FOUNDER"
                && (u.destination || "").trim().toLowerCase() !== "india"
                && (u.destination || "").trim() !== "" && (
                (u.permissions || []).includes("attendance") ? (
                  <button data-testid={`salary-revoke-${u.id}`} onClick={() => revokeSalaryAccess(u)}
                    className="mt-2 w-full flex items-center justify-center gap-2 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 py-2 rounded-md hover:bg-emerald-100 font-semibold"
                    title="Attendance & Salary access is ON — click to revoke">
                    <Wallet size={12}/> Salary access ON · click to revoke
                  </button>
                ) : (
                  <button data-testid={`salary-grant-${u.id}`} onClick={() => grantSalaryAccess(u)}
                    className="mt-2 w-full flex items-center justify-center gap-2 text-xs bg-amber-50 text-amber-800 border border-amber-200 py-2 rounded-md hover:bg-amber-100 font-semibold">
                    <Wallet size={12}/> Grant salary access
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Participants section */}
      {isFounder && (
        <div className="aii-card p-5" data-testid="participants-section">
          <button onClick={()=>setParticipantsOpen(o=>!o)} className="w-full flex items-center justify-between" data-testid="participants-toggle">
            <div className="flex items-center gap-2">
              <Plane size={16} className="text-brand"/>
              <h2 className="font-display font-semibold text-lg">Participants ({participants.length})</h2>
              <span className="text-xs text-[color:var(--aii-muted)] hidden sm:inline">— self-service portal users</span>
            </div>
            {participantsOpen ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
          </button>
          {participantsOpen && (
            <div className="mt-4 overflow-x-auto">
              {participants.length === 0 ? (
                <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center">No participants yet. Provision one from the <a href="/program-bookings" className="text-brand underline">Program Bookings</a> page.</div>
              ) : (
                <table className="aii-table" style={{ minWidth: 640 }}>
                  <thead><tr><th>Name</th><th>Programme</th><th>Dates</th><th>Login email</th><th>Placement</th><th className="text-right">Actions</th></tr></thead>
                  <tbody>
                    {participants.map(p => (
                      <tr key={p.id} data-testid={`participant-row-${p.id}`}>
                        <td className="font-medium">{p.name}</td>
                        <td>{p.booking?.programme || "—"}</td>
                        <td className="text-xs">{fmtDate(p.booking?.start_date) || "?"} → {fmtDate(p.booking?.end_date) || "?"}</td>
                        <td className="text-xs">{p.email}</td>
                        <td className="text-xs">{p.booking?.project_placement || <span className="text-[color:var(--aii-muted)] italic">unassigned</span>}</td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={()=>copyParticipantCreds(p)} data-testid={`participant-copy-${p.id}`} title="Copy fresh credentials" className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"><Copy size={11}/> Copy</button>
                            <button onClick={()=>sendParticipantCreds(p)} data-testid={`participant-send-${p.id}`} title="Email + WhatsApp fresh credentials" className="text-xs px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"><Send size={11}/> Send</button>
                            <button onClick={()=>deleteParticipant(p)} data-testid={`participant-delete-${p.id}`} title="Revoke portal access (booking data is kept)" className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5"><Trash2 size={13}/></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Edit permissions modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-auto" data-testid="team-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit {editing.name}</h3>
              <button onClick={cancelEdit}><X size={18} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Name</label>
                <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Role</label>
                <select value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value })} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                  {ROLES.filter(r => r !== "FOUNDER").map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold flex items-center gap-1"><MapPin size={10}/> Destination (country)</label>
                <select data-testid="team-edit-destination" value={editing.destination || ""} onChange={e => setEditing({ ...editing, destination: e.target.value })} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                  <option value="">— unassigned (sees all) —</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold flex items-center gap-1"><MapPin size={10}/> City / office</label>
                <input data-testid="team-edit-city" value={editing.city || ""} onChange={e => setEditing({ ...editing, city: e.target.value })} placeholder="e.g. Jaipur" className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
              </div>
            </div>
            <p className="text-[11px] text-[color:var(--aii-muted)] mb-3 -mt-1 flex items-center gap-1"><MapPin size={11}/> Assigning a location auto-scopes every operational list this member sees. Leave blank if this member should see all offices.</p>
            <div>
              <div className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold mb-2">Extra module access</div>
              <p className="text-xs text-[color:var(--aii-muted)] mb-3">By default a role sees only its own modules. Check any extras this person should see.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {modules.map(mod => (
                  <label key={mod} className="flex items-center gap-2 text-sm py-1.5 px-2 border border-[color:var(--aii-border)] rounded-md cursor-pointer hover:bg-[color:var(--aii-green-tint)]">
                    <input type="checkbox" checked={editing.permissions.includes(mod)} onChange={() => togglePerm(mod)} data-testid={`perm-${mod}`} />
                    <span>{MODULE_LABEL[mod] || mod}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end mt-6 gap-2">
              <button onClick={cancelEdit} className="px-4 py-2 text-sm">Cancel</button>
              <button data-testid="team-save-perm" onClick={savePermissions} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark flex items-center gap-1.5"><Save size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Password reset */}
      {pwdReset.open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2"><KeyRound size={16} /> Reset password</h3>
              <button onClick={() => setPwdReset({ open: false, uid: null, pwd: "" })}><X size={18} /></button>
            </div>
            <input data-testid="team-pwd-reset" type="text" value={pwdReset.pwd} onChange={e => setPwdReset(p => ({ ...p, pwd: e.target.value }))} placeholder="New password" className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPwdReset({ open: false, uid: null, pwd: "" })} className="px-4 py-2 text-sm">Cancel</button>
              <button data-testid="team-pwd-save" onClick={submitPwd} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark">Set password</button>
            </div>
          </div>
        </div>
      )}
      {isFounder && (
        <div className="aii-card p-5" data-testid="security-audit-section">
          <button onClick={()=>setAuditOpen(o=>!o)} className="w-full flex items-center justify-between" data-testid="audit-toggle">
            <div className="flex items-center gap-2">
              <ShieldAlert size={16} className="text-brand"/>
              <h2 className="font-display font-semibold text-lg">Security audit</h2>
              <span className="text-xs text-[color:var(--aii-muted)] hidden sm:inline">— email/password changes across the team</span>
            </div>
            {auditOpen ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
          </button>
          {auditOpen && (
            <div className="mt-4">
              {audit.length === 0 ? (
                <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center">No security events logged yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="aii-table" style={{ minWidth: 640 }}>
                    <thead><tr><th>When</th><th>Who</th><th>Action</th><th>IP</th><th>Details</th></tr></thead>
                    <tbody>
                      {audit.map((r) => (
                        <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                          <td className="text-xs whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                          <td className="text-xs">{r.user_name} <span className="text-[color:var(--aii-muted)]">({r.user_email})</span></td>
                          <td><span className={`pill ${r.action === "change_password" ? "pill-red" : "pill-amber"}`}>{r.action.replace(/_/g, " ")}</span></td>
                          <td className="text-xs font-mono">{r.ip || "—"}</td>
                          <td className="text-xs">{r.extra?.old ? `${r.extra.old} → ${r.extra.new}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}


    </div>
  );
}

// ============================================================================
// ============================================================================
// Office Setup Checklist — Founder-only widget.
// Fetches /api/office-checklists (live-derived) and renders one collapsible
// card per (destination, city) with a progress bar + tick-marks. Items are
// auto-derived: as soon as an office adds a PCWM / accommodation / fills
// staff phones, the item ticks itself on next reload.
// Also exposes a **Delete office** action so a Founder can nuke a whole
// office (staff + accommodations + rooms) in one Recycle-Bin-backed shot.
// ============================================================================
function OfficeChecklists({ onOfficeDeleted }) {
  const [checklists, setChecklists] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [loading, setLoading] = useState(true);
  // Type-to-confirm delete state: null when closed, otherwise the office
  // being nuked. Stashed here rather than a page-level state so the
  // checklist widget owns its own destructive UI end-to-end.
  const [delTarget, setDelTarget] = useState(null);
  const [delType, setDelType] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get("/office-checklists")
      .then(({ data }) => setChecklists(data || []))
      .catch(() => setChecklists([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDelBusy(true);
    try {
      const dest = encodeURIComponent(delTarget.office);
      const city = encodeURIComponent(delTarget.city || "");
      const { data } = await api.delete(`/office-checklists/${dest}/${city}`);
      toast.success(`Office deleted — ${data.staff_deleted} staff + ${data.accommodations_deleted} accommodation(s). Restore from Recycle Bin.`);
      setDelTarget(null); setDelType("");
      reload();
      onOfficeDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setDelBusy(false); }
  };

  if (loading) return <div className="aii-card p-4 text-sm text-[color:var(--aii-muted)]">Loading office setup…</div>;
  if (!checklists.length) return null;
  const defaultOpenKey = checklists.find(c => !c.complete);
  const activeKey = openKey ?? (defaultOpenKey ? `${defaultOpenKey.office}·${defaultOpenKey.city}` : null);
  // Confirm phrase — must exactly match "DELETE <office>[ - <city>]" so a
  // Founder can't nuke an office with a stray Enter press.
  const confirmPhrase = delTarget ? `DELETE ${delTarget.office}${delTarget.city ? ` - ${delTarget.city}` : ""}` : "";
  return (
    <div className="aii-card p-5" data-testid="office-checklists">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList size={16} className="text-brand"/>
        <h3 className="font-semibold">Office setup checklist</h3>
      </div>
      <p className="text-xs text-[color:var(--aii-muted)] mb-3">
        Live tick-marks: items auto-check as each office adds a PCWM, seeds accommodation,
        or fills staff phones — nothing to save.
      </p>
      <div className="space-y-2">
        {checklists.map(c => {
          const key = `${c.office}·${c.city}`;
          const pct = Math.round((c.done / c.total) * 100);
          const open = activeKey === key;
          return (
            <div key={key}
                 className={`border rounded-md overflow-hidden ${c.complete ? "border-emerald-200 bg-emerald-50/40" : "border-[color:var(--aii-border)]"}`}
                 data-testid={`office-checklist-${c.office.toLowerCase()}`}>
              <button type="button"
                      onClick={() => setOpenKey(open ? null : key)}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[color:var(--aii-green-tint)]/30">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <MapPin size={13} className="text-brand flex-shrink-0"/>
                  <span className="font-semibold text-sm truncate">
                    {c.office}{c.city && ` · ${c.city}`}
                  </span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold ${c.complete ? "text-emerald-700" : "text-amber-700"}`}>
                    {c.done}/{c.total}
                  </span>
                  <span className="text-[10px] text-[color:var(--aii-muted)] font-normal">
                    · {c.staff_count} staff
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-24 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div className={`h-full ${c.complete ? "bg-emerald-500" : "bg-brand"}`} style={{ width: `${pct}%` }}/>
                  </div>
                  <button type="button"
                          onClick={(e) => { e.stopPropagation(); setDelTarget(c); setDelType(""); }}
                          data-testid={`office-delete-${c.office.toLowerCase()}`}
                          title={`Delete this office (${c.staff_count} staff + accommodations)`}
                          className="p-1 rounded hover:bg-red-100 text-[color:var(--aii-muted)] hover:text-red-600">
                    <Trash2 size={13}/>
                  </button>
                  {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                </div>
              </button>
              {open && (
                <ul className="border-t border-[color:var(--aii-border)] divide-y divide-[color:var(--aii-border)]">
                  {c.items.map(it => (
                    <li key={it.key}
                        data-testid={`checklist-item-${c.office.toLowerCase()}-${it.key}`}
                        className={`flex items-start gap-2.5 px-3 py-2 text-xs ${it.done ? "text-[color:var(--aii-muted)]" : "text-[color:var(--aii-text-1)]"}`}>
                      {it.done
                        ? <Check size={13} className="text-emerald-600 mt-0.5 flex-shrink-0"/>
                        : <Circle size={13} className="text-amber-500 mt-0.5 flex-shrink-0"/>}
                      <div className="flex-1 min-w-0">
                        <div className={it.done ? "line-through" : "font-medium"}>{it.label}</div>
                        {it.detail && <div className="text-[10px] text-[color:var(--aii-muted)] mt-0.5">{it.detail}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {delTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => !delBusy && setDelTarget(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="office-delete-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] bg-red-50">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2 text-red-800">
                <AlertOctagon size={18} className="text-red-600"/> Delete this office?
              </h3>
              <p className="text-xs text-red-800 mt-1">
                This removes every <b>{delTarget.staff_count}</b> staff account (BDOM / PCWM / GEHM / SMCM)
                and every accommodation + room tied to <b>{delTarget.office}{delTarget.city && ` · ${delTarget.city}`}</b>.
                Program bookings and arrivals stay put. You can restore the whole office from the Recycle Bin.
              </p>
            </div>
            <div className="p-4 space-y-3">
              <label className="text-xs font-semibold text-[color:var(--aii-text-1)] block">
                To confirm, type <code className="text-red-700 bg-red-50 px-1 rounded">{confirmPhrase}</code>
              </label>
              <input
                data-testid="office-delete-confirm-input"
                value={delType}
                onChange={e => setDelType(e.target.value)}
                autoFocus
                placeholder={confirmPhrase}
                className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm font-mono"/>
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setDelTarget(null)} disabled={delBusy}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                data-testid="office-delete-submit"
                onClick={confirmDelete}
                disabled={delBusy || delType.trim() !== confirmPhrase}
                className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">
                <Trash2 size={13}/> {delBusy ? "Deleting…" : "Delete office"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

